import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SimpleGrid,
  Paper,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Badge,
  Button,
  Tooltip,
  Loader,
  Alert,
  RingProgress
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconFileText,
  IconAlignLeft,
  IconDatabase,
  IconFileZip,
  IconRefresh,
  IconCheck,
  IconAlertTriangle,
  IconClock,
  IconInfoCircle,
  IconBrain
} from '@tabler/icons-react';
import { aiKnowledgeApi, KBStats } from '@/api/aiKnowledge.api';

interface OverviewTabProps {
  refreshKey: number;
}

const OverviewTab = ({ refreshKey }: OverviewTabProps) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<KBStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [reindexing, setReindexing] = useState(false);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await aiKnowledgeApi.getStats();
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [refreshKey]);

  const handleReindexAll = async () => {
    try {
      setReindexing(true);
      const response = await aiKnowledgeApi.reindexAll();
      if (response.data.success) {
        notifications.show({
          title: t('common.success'),
          message: response.data.message,
          color: 'green',
          icon: <IconCheck size={18} />
        });
        // Refresh stats after a delay
        setTimeout(fetchStats, 3000);
      }
    } catch (error) {
      notifications.show({
        title: t('common.error'),
        message: t('aiKnowledge.errors.reindexFailed'),
        color: 'red'
      });
    } finally {
      setReindexing(false);
    }
  };

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="lg" />
      </Group>
    );
  }

  const statCards = [
    {
      label: t('aiKnowledge.stats.totalDocuments'),
      value: stats?.totalDocuments || 0,
      icon: IconFileText,
      color: 'blue',
      description: t('aiKnowledge.stats.totalDocumentsDesc')
    },
    {
      label: t('aiKnowledge.stats.totalTextEntries'),
      value: stats?.totalTextEntries || 0,
      icon: IconAlignLeft,
      color: 'violet',
      description: t('aiKnowledge.stats.totalTextEntriesDesc')
    },
    {
      label: t('aiKnowledge.stats.totalChunks'),
      value: stats?.totalChunksInDB || 0,
      icon: IconDatabase,
      color: 'teal',
      description: t('aiKnowledge.stats.totalChunksDesc')
    },
    {
      label: t('aiKnowledge.stats.totalSize'),
      value: `${stats?.totalFileSizeMB || 0} MB`,
      icon: IconFileZip,
      color: 'orange',
      description: t('aiKnowledge.stats.totalSizeDesc')
    }
  ];

  const indexedDocs = stats?.documentsByStatus?.indexed || 0;
  const errorDocs = stats?.documentsByStatus?.error || 0;
  const processingDocs = stats?.documentsByStatus?.processing || 0;
  const pendingDocs = stats?.documentsByStatus?.pending || 0;
  const totalDocs = stats?.totalDocuments || 0;
  const indexedPercent = totalDocs > 0 ? Math.round((indexedDocs / totalDocs) * 100) : 0;

  return (
    <Stack gap="lg">
      {/* Stat cards */}
      <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="md">
        {statCards.map((card) => (
          <Tooltip key={card.label} label={card.description} multiline w={250} position="bottom">
            <Paper
              p="md"
              radius="md"
              withBorder
              style={{ cursor: 'help' }}
            >
              <Group justify="space-between" align="flex-start">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    {card.label}
                  </Text>
                  <Text size="xl" fw={700} mt={4}>
                    {card.value}
                  </Text>
                </div>
                <ThemeIcon size={40} radius="md" variant="light" color={card.color}>
                  <card.icon size={22} />
                </ThemeIcon>
              </Group>
            </Paper>
          </Tooltip>
        ))}
      </SimpleGrid>

      {/* Status & ChromaDB stats */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        {/* Document status */}
        <Paper p="lg" radius="md" withBorder>
          <Text fw={600} mb="md">{t('aiKnowledge.stats.documentStatus')}</Text>
          <Group align="center" gap="xl">
            <RingProgress
              size={120}
              thickness={12}
              roundCaps
              sections={totalDocs > 0 ? [
                { value: (indexedDocs / totalDocs) * 100, color: 'green' },
                { value: (processingDocs / totalDocs) * 100, color: 'yellow' },
                { value: (errorDocs / totalDocs) * 100, color: 'red' },
                { value: (pendingDocs / totalDocs) * 100, color: 'gray' },
              ] : [{ value: 100, color: 'gray.3' }]}
              label={
                <Text ta="center" fw={700} size="lg">
                  {indexedPercent}%
                </Text>
              }
            />
            <Stack gap="xs">
              <Group gap="xs">
                <Badge size="sm" color="green" variant="dot">
                  <IconCheck size={12} /> {indexedDocs} {t('aiKnowledge.stats.indexed')}
                </Badge>
              </Group>
              <Group gap="xs">
                <Badge size="sm" color="yellow" variant="dot">
                  <IconClock size={12} /> {processingDocs} {t('aiKnowledge.stats.processing')}
                </Badge>
              </Group>
              <Group gap="xs">
                <Badge size="sm" color="red" variant="dot">
                  <IconAlertTriangle size={12} /> {errorDocs} {t('aiKnowledge.stats.errors')}
                </Badge>
              </Group>
              {pendingDocs > 0 && (
                <Group gap="xs">
                  <Badge size="sm" color="gray" variant="dot">
                    {pendingDocs} {t('aiKnowledge.stats.pending')}
                  </Badge>
                </Group>
              )}
            </Stack>
          </Group>
        </Paper>

        {/* ChromaDB / Vector DB stats */}
        <Paper p="lg" radius="md" withBorder>
          <Group justify="space-between" mb="md">
            <Text fw={600}>{t('aiKnowledge.stats.vectorDatabase')}</Text>
            <Tooltip label={t('aiKnowledge.tooltips.vectorDbHelp')}>
              <ThemeIcon variant="subtle" size="sm" color="gray">
                <IconInfoCircle size={16} />
              </ThemeIcon>
            </Tooltip>
          </Group>
          <Stack gap="sm">
            <Group justify="space-between">
              <Group gap="xs">
                <IconBrain size={16} color="var(--mantine-color-blue-5)" />
                <Text size="sm">{t('aiKnowledge.stats.totalVectorChunks')}</Text>
              </Group>
              <Text fw={600}>{stats?.totalChunksInChroma || 0}</Text>
            </Group>
            <Group justify="space-between">
              <Group gap="xs">
                <IconFileText size={16} color="var(--mantine-color-violet-5)" />
                <Text size="sm">{t('aiKnowledge.stats.knowledgeChunks')}</Text>
              </Group>
              <Text fw={600}>
                {(stats?.knowledgeDocChunks || 0) + (stats?.knowledgeTextChunks || 0)}
              </Text>
            </Group>
            <Group justify="space-between">
              <Group gap="xs">
                <IconDatabase size={16} color="var(--mantine-color-teal-5)" />
                <Text size="sm">{t('aiKnowledge.stats.propertyChunks')}</Text>
              </Group>
              <Text fw={600}>{stats?.propertyChunks || 0}</Text>
            </Group>
            {stats?.lastIndexedAt && (
              <Group justify="space-between">
                <Text size="sm" c="dimmed">{t('aiKnowledge.stats.lastIndexed')}</Text>
                <Text size="sm" c="dimmed">
                  {new Date(stats.lastIndexedAt).toLocaleString()}
                </Text>
              </Group>
            )}
          </Stack>
        </Paper>
      </SimpleGrid>

      {/* File types breakdown */}
      {stats?.documentsByType && Object.keys(stats.documentsByType).length > 0 && (
        <Paper p="lg" radius="md" withBorder>
          <Text fw={600} mb="md">{t('aiKnowledge.stats.documentTypes')}</Text>
          <Group gap="sm">
            {Object.entries(stats.documentsByType).map(([type, count]) => (
              <Badge key={type} size="lg" variant="light" color="blue">
                {type.toUpperCase()}: {count}
              </Badge>
            ))}
          </Group>
        </Paper>
      )}

      {/* Actions */}
      <Paper p="lg" radius="md" withBorder>
        <Group justify="space-between" align="center">
          <div>
            <Text fw={600}>{t('aiKnowledge.actions.reindexTitle')}</Text>
            <Text size="sm" c="dimmed">
              {t('aiKnowledge.actions.reindexDescription')}
            </Text>
          </div>
          <Button
            leftSection={<IconRefresh size={16} />}
            loading={reindexing}
            onClick={handleReindexAll}
            variant="light"
            color="blue"
          >
            {t('aiKnowledge.actions.reindexAll')}
          </Button>
        </Group>
      </Paper>

      {/* How RAG works explanation */}
      <Alert
        variant="light"
        color="violet"
        icon={<IconBrain size={20} />}
        title={t('aiKnowledge.howItWorks.ragTitle')}
      >
        <Stack gap="xs" mt="xs">
          {[1, 2, 3, 4, 5].map((step) => (
            <Group key={step} gap="xs" align="flex-start">
              <Badge size="sm" circle variant="filled" color="violet">
                {step}
              </Badge>
              <Text size="sm" style={{ flex: 1 }}>
                {t(`aiKnowledge.howItWorks.step${step}`)}
              </Text>
            </Group>
          ))}
        </Stack>
      </Alert>
    </Stack>
  );
};

export default OverviewTab;
